import React, { type SyntheticEvent, type ReactNode, useCallback } from 'react';
import { xcss, Pressable } from '@atlaskit/primitives';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { asyncCreateIssueTypeModalEntryPoint } from '@atlassian/jira-create-issue-type-modal/entrypoint.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import {
	type ConfigureSource,
	GIC_VIEW_NO_RESULTS_SOURCE,
} from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import type { UFOExperience } from '@atlassian/ufo';
import { fg } from '@atlassian/jira-feature-gating';
import { defaultModalProps } from '@atlassian/jira-issue-type-config-common/src/constants.tsx';

type CreateWorkTypeButtonProps = {
	children: ReactNode;
	onClick: () => void;
	onMouseEnter: () => void;
	entryPointProps: {
		onIssueTypeCreated: () => void;
		issueTypesCountLimit: number;
		onClose: () => void;
		onOpen: () => void;
		source: ConfigureSource;
		ufoExperience: UFOExperience;
		shouldFetchProjectData: boolean;
	};
	entryPointParams: {
		cloudId: string;
		projectKey: string;
	};
};

export const CreateWorkTypeButton = ({
	children,
	onClick,
	onMouseEnter,
	entryPointProps,
	entryPointParams,
}: CreateWorkTypeButtonProps) => {
	const handleClick = useCallback(
		(e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'createIssueTypeButton', {
				calledFrom: GIC_VIEW_NO_RESULTS_SOURCE,
			});
			if (!fg('jira_gic_inline_create_work_type_refactor')) {
				onClick();
			}
		},
		[onClick],
	);

	if (!fg('jira_gic_inline_create_work_type_refactor')) {
		return (
			<Pressable
				onClick={handleClick}
				onMouseEnter={onMouseEnter}
				xcss={createWorkTypeInlineNoResultStyles}
			>
				{children}
			</Pressable>
		);
	}

	return (
		// TODO (JGFENG-1237) Migrate deprecated ModalEntryPointPressableTrigger
		<ModalEntryPointPressableTrigger
			entryPoint={asyncCreateIssueTypeModalEntryPoint}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			interactionName="create-issue-type-modal"
			modalProps={defaultModalProps}
			useInternalModal={false}
		>
			{({ ref }) => (
				<Pressable
					onClick={handleClick}
					xcss={createWorkTypeInlineNoResultStyles}
					// @ts-expect-error - The reference type from ModalEntryPointPressableTrigger is incompatible with Pressable,
					// but this works correctly at runtime and is a type-system limitation
					ref={ref}
				>
					{children}
				</Pressable>
			)}
		</ModalEntryPointPressableTrigger>
	);
};

const createWorkTypeInlineNoResultStyles = xcss({
	paddingBlockStart: 'space.0',
	paddingInline: 'space.0',
	marginTop: 'space.100',
	borderRadius: token('space.025'),
	color: 'color.link',
	backgroundColor: 'color.background.neutral.subtle',
	display: 'block',
	fontWeight: 'font.weight.bold',
});
